import React from 'react';
import type { IntelligenceReport } from '@akem/shared';
import { Box, Chip, Divider, Grid, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type IntelligenceReportDetailsVariant = 'widget' | 'profile';
export type HistoricalEvidenceLayout = 'table' | 'stack';

interface IntelligenceReportDetailsProps {
  report: IntelligenceReport;
  variant?: IntelligenceReportDetailsVariant;
  spacing?: number;
  historicalLayout?: HistoricalEvidenceLayout;
  analysisEmptyText?: string;
  analysisLayout?: 'columns' | 'stack';
}

const AnalysisColumn: React.FC<{
  title: string;
  bullets: string[];
  color: string;
  emptyText: string;
}> = ({ title, bullets, color, emptyText }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 1.5,
      height: '100%',
      bgcolor: 'background.paper',
    }}
  >
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="subtitle2" sx={{ color, fontWeight: 600 }}>
          {title}
        </Typography>
      </Stack>
      <Stack component="ul" spacing={0.6} sx={{ m: 0, pl: 2 }}>
        {Array.isArray(bullets) && bullets.length > 0 ? (
          bullets.map((item, idx) => (
            <Typography key={`${title}-${idx}`} component="li" variant="body2" color="text.secondary">
              {item}
            </Typography>
          ))
        ) : (
          <Typography component="li" variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        )}
      </Stack>
    </Stack>
  </Box>
);

const Section: React.FC<{
  title: string;
  spacing?: number;
  headerAdornment?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, spacing = 1.5, headerAdornment, children }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 2,
      p: { xs: 1.5, sm: 2 },
      bgcolor: 'background.default',
    }}
  >
    <Stack spacing={spacing}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary' }}>
          {title}
        </Typography>
        {headerAdornment}
      </Stack>
      {children}
    </Stack>
  </Paper>
);

const renderHistoricalEvidence = (
  layout: HistoricalEvidenceLayout,
  report: IntelligenceReport
) => {
  if (!Array.isArray(report.historicalEvidence) || report.historicalEvidence.length === 0) {
    return null;
  }

  if (layout === 'stack') {
    return (
      <Section title="Historical Evidence" spacing={1.5}>
        <Stack spacing={1.5} divider={<Divider flexItem sx={{ borderColor: 'divider', opacity: 0.6 }} />}>
          {report.historicalEvidence.map((row, idx) => (
            <Box key={`${row.example}-${idx}`} sx={{ display: 'grid', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.example}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.period ?? 'Period not specified'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Relevance: {row.relevance ?? 'Unspecified'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Section>
    );
  }

  return (
    <Section title="Historical Evidence" spacing={1.5}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: 480,
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& th': {
              textAlign: 'left',
              bgcolor: 'background.paper',
              color: 'text.secondary',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              whiteSpace: 'nowrap',
            },
            '& td, & th': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              p: 1.25,
              verticalAlign: 'top',
            },
            '& tr:last-of-type td': {
              borderBottom: 'none',
            },
          }}
        >
          <thead>
            <tr>
              <th>Example</th>
              <th>Period</th>
              <th>Description</th>
              <th>Relevance</th>
            </tr>
          </thead>
          <tbody>
            {report.historicalEvidence.map((row, idx) => (
              <tr key={`${row.example}-${idx}`}>
                <td>{row.example}</td>
                <td>{row.period ?? '—'}</td>
                <td>{row.description}</td>
                <td>{row.relevance ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Section>
  );
};

export const IntelligenceReportDetails: React.FC<IntelligenceReportDetailsProps> = ({
  report,
  variant = 'widget',
  spacing,
  historicalLayout,
  analysisEmptyText,
  analysisLayout,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const resolvedSpacing = spacing ?? (variant === 'profile' ? 2.5 : 3);
  const resolvedAnalysisEmptyText = analysisEmptyText ?? (variant === 'profile' ? 'No insights available.' : 'No commentary');
  const resolvedHistoricalLayout = historicalLayout ?? (variant === 'profile' ? 'stack' : 'table');
  const decisionSpacing = variant === 'profile' ? 0.5 : 1;
  const summarizeSpacing = variant === 'profile' ? 0.5 : 1;
  const emphasizeDecision = variant === 'profile';
  const resolvedAnalysisLayout: 'columns' | 'stack' = analysisLayout ?? (variant === 'profile' ? 'stack' : 'columns');
  const analysisMdCols = resolvedAnalysisLayout === 'stack' ? 12 : 4;
  const responsiveHistoricalLayout: HistoricalEvidenceLayout = isMdUp ? resolvedHistoricalLayout : 'stack';

  return (
    <Stack spacing={resolvedSpacing}>
      <Section title="Objective Analysis" spacing={1.5}>
        <Box sx={{ px: { xs: 0.75, sm: 1.25 } }}>
          <Grid container spacing={2}>
          <Grid item xs={12} md={analysisMdCols}>
            <AnalysisColumn
              title="Positive"
              bullets={report.objective.positive}
              color="success.main"
              emptyText={resolvedAnalysisEmptyText}
            />
          </Grid>
          <Grid item xs={12} md={analysisMdCols}>
            <AnalysisColumn
              title="Neutral"
              bullets={report.objective.neutral}
              color="text.secondary"
              emptyText={resolvedAnalysisEmptyText}
            />
          </Grid>
          <Grid item xs={12} md={analysisMdCols}>
            <AnalysisColumn
              title="Negative"
              bullets={report.objective.negative}
              color="error.main"
              emptyText={resolvedAnalysisEmptyText}
            />
          </Grid>
          </Grid>
        </Box>
      </Section>

      <Section
        title="Decision"
        spacing={decisionSpacing + 0.75}
        headerAdornment={
          <Chip
            label={(report.decision.picked ?? 'Unspecified').toUpperCase()}
            color={emphasizeDecision ? 'primary' : 'default'}
            variant={emphasizeDecision ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
        }
      >
        <Typography variant="body2" color="text.secondary">
          {report.decision.rationale}
        </Typography>
      </Section>

      <Section title="Summary & Likely Outcome" spacing={summarizeSpacing + 0.5}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              Summary
            </Typography>
            <Typography variant="body2">{report.summary}</Typography>
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              Likely Outcome
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {report.likelyOutcome}
            </Typography>
          </Box>
        </Box>
      </Section>

      {renderHistoricalEvidence(responsiveHistoricalLayout, report)}

      {Array.isArray(report.sources) && report.sources.length ? (
        <Section title="Sources" spacing={1.25}>
          <Stack
            spacing={1}
            divider={<Divider flexItem sx={{ borderColor: 'divider', opacity: 0.6 }} />}
          >
            {report.sources.map((src, idx) => (
              <Typography key={`${src.title}-${idx}`} variant="body2" color="text.secondary">
                {src.title}
                {src.source ? ` — ${src.source}` : ''}
              </Typography>
            ))}
          </Stack>
        </Section>
      ) : null}
    </Stack>
  );
};

export default IntelligenceReportDetails;
